import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['placeholder', 'input']

  declare readonly placeholderTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement

  private readonly canvas: HTMLCanvasElement = document.createElement('canvas')
  private rafId = 0
  private resizeObserver?: ResizeObserver
  private mutationObserver?: MutationObserver

  private readonly last = { offset: -1, pad: -1, font: '' }

  private readonly onInput: () => void = () => { this.schedule() }

  connect (): void {
    this.cacheFont()

    this.inputTarget.addEventListener('input', this.onInput, { passive: true })

    this.resizeObserver = new ResizeObserver(() => this.schedule())
    this.resizeObserver.observe(this.inputTarget)

    this.mutationObserver = new MutationObserver(() => {
      this.schedule()
    })

    this.mutationObserver.observe(this.placeholderTarget, {
      characterData: true,
      childList: true,
      subtree: true
    })

    this.schedule()
  }

  disconnect (): void {
    this.inputTarget.removeEventListener('input', this.onInput)
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
    if (this.rafId !== 0) {
      cancelAnimationFrame(this.rafId)
    }
    this.rafId = 0
  }

  private schedule (): void {
    if (this.rafId !== 0) return
    this.rafId = requestAnimationFrame((): void => {
      this.rafId = 0
      this.reposition()
    })
  }

  private reposition (): void {
    const input = this.inputTarget
    const ph = this.placeholderTarget

    const currentFont = this.getCanvasFont()
    if (currentFont !== this.last.font) {
      this.cacheFont()
    }

    const gapVar = getComputedStyle(ph).getPropertyValue('--dp-gap')
    const parsedGap = Number.parseFloat(gapVar)
    const gap = Number.isFinite(parsedGap) ? parsedGap : 15

    const value = input.value.length > 0 ? input.value : input.placeholder
    const valueWidth = Math.round(this.textWidth(value))
    const inputWidth = input.offsetWidth

    const phText = ph.innerText.trim()
    const placeholderWidth = this.textWidth(phText) | 0

    const maxLeft = Math.max(0, inputWidth - placeholderWidth - gap) | 0
    const offset = Math.min(maxLeft, valueWidth + gap) | 0
    const padRight = placeholderWidth + gap

    if (padRight !== this.last.pad) {
      input.style.setProperty('padding-right', `${padRight}px`, 'important')
      this.last.pad = padRight
    }

    if (offset !== this.last.offset) {
      ph.style.setProperty('--dp-x', `${offset}px`)
      this.last.offset = offset
    }
  }

  private cacheFont (): void {
    const font = this.getCanvasFont()
    const ctx = this.canvas.getContext('2d')
    if (ctx == null) return
    ctx.font = font
    this.last.font = font
  }

  private textWidth (text: string): number {
    const ctx = this.canvas.getContext('2d')
    if (ctx == null) return 0
    return ctx.measureText(text).width
  }

  private getCanvasFont (): string {
    const cs = getComputedStyle(this.inputTarget)
    return `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
  }
}
